import React from "react"
import { Typography } from "@mui/material"
import { DynamicConfigField, DynamicConfigKey, MeetingServiceConfig } from "../../form/formTypes"
import { SelectedMainUserState } from "../../../createOrderTypes"
import { isMeetingTimeKey, isRenderableConfigField } from "../../form/helpers"
import { FieldRenderer } from "./FieldRenderer"
import { WorkgroupSelect } from "../meetingDetails/WorkgroupSelect"

type GroupFieldsProps = {
    title?: string
    fields: DynamicConfigField[]
    meetingServiceConfiguration: MeetingServiceConfig
    user: SelectedMainUserState
    defaultButtonClass?: string
    deliveryFields: {
        deliveryLocations?: DynamicConfigField
        otherDeliveryLocation?: DynamicConfigField
    }
    showOtherLocationField: boolean
    setShowOtherLocationField: (v: boolean) => void
    hasMeetingLocationRequired?: boolean
    isSpecialOrder?: boolean
    deferDeliveryLocationFields?: boolean
    deferMeetingTimeFields?: boolean
    withWorkgroup?: boolean
}

export function GroupFields({
    title,
    fields,
    meetingServiceConfiguration,
    user,
    defaultButtonClass,
    deliveryFields,
    showOtherLocationField,
    setShowOtherLocationField,
    hasMeetingLocationRequired,
    isSpecialOrder,
    deferDeliveryLocationFields,
    deferMeetingTimeFields,
    withWorkgroup,
}: GroupFieldsProps) {
    const isRecurrence = fields.some((f) => f.key === DynamicConfigKey.RECURRENCY)
    const isCreateBuyerParty = fields.find((el) => el.key === DynamicConfigKey.CREATE_BUYER_PARTY)?.active
    const showGroupTitle = Boolean(title) && !isRecurrence && fields.some(isRenderableConfigField)

    const deferredKeys = new Set<string>()
    if (deferDeliveryLocationFields) {
        deferredKeys.add(DynamicConfigKey.DELIVERY_LOCATIONS)
        deferredKeys.add(DynamicConfigKey.OTHER_DELIVERY_LOCATION)
    }
    if (deferMeetingTimeFields) {
        deferredKeys.add(DynamicConfigKey.MEETING_TIME)
        deferredKeys.add(DynamicConfigKey.MEETING_START_TIME)
        deferredKeys.add(DynamicConfigKey.MEETING_END_TIME)
    }

    const keptFields = deferredKeys.size > 0 ? fields.filter((f) => !deferredKeys.has(f.key)) : fields

    // Start and end are rendered as one pair, so only the first meeting-time field renders it.
    const firstMeetingTimeField = keptFields.find((f) => isMeetingTimeKey(f.key) && isRenderableConfigField(f))
    const renderedFields = keptFields.filter((f) => !isMeetingTimeKey(f.key) || f === firstMeetingTimeField)

    // Workgroup follows the responsible field; without one in the config it closes the group.
    const hasResponsibleField = renderedFields.some((f) => f.key === DynamicConfigKey.MEETING_RESPONSIBLE)

    return (
        <>
            {showGroupTitle && <Typography variant="subtitle1">{title}</Typography>}
            {renderedFields.map((config) => (
                <React.Fragment key={config.key}>
                    <FieldRenderer
                        config={config}
                        groupTitle={title}
                        meetingServiceConfiguration={meetingServiceConfiguration}
                        user={user}
                        defaultButtonClass={defaultButtonClass}
                        deliveryFields={deliveryFields}
                        showOtherLocationField={showOtherLocationField}
                        setShowOtherLocationField={setShowOtherLocationField}
                        isCreateBuyerParty={isCreateBuyerParty}
                        hasMeetingLocationRequired={hasMeetingLocationRequired}
                        isSpecialOrder={isSpecialOrder}
                    />
                    {withWorkgroup && config.key === DynamicConfigKey.MEETING_RESPONSIBLE && <WorkgroupSelect />}
                </React.Fragment>
            ))}
            {withWorkgroup && !hasResponsibleField && <WorkgroupSelect />}
        </>
    )
}
